import type Product from "@/models/Product";
import ProductImage from "@/models/ProductImage";
import type { UseSeoMetaInput } from "@unhead/vue";

export interface SeoData {
  title?: string | null;
  image?: string | null;
  lcpImage?: string | null;
  description?: string | null;
  url?: string | null;
  type?: string | null;
  noIndex?: boolean;
}

const fixData = (data: SeoData) => {
  const title = data.title || "Digitalife";
  const image = data.image || "/images/logo-square.jpg";
  const lcpImage = data.lcpImage;
  const type = data.type || "website";
  const description = data.description;
  const url = data.url || "https://" + import.meta.env.VITE_APP_DOMAIN;
  const noIndex = data.noIndex || false;
  return { title, image, lcpImage, type, description, url, noIndex };
};

export class SeoService {
  public setMeta(
    propName: string,
    content: string | undefined | null,
    common?: boolean,
  ) {
    let metaTag = document.querySelector(
      common ? `meta[name="${propName}"]` : `meta[property="${propName}"]`,
    ) as HTMLMetaElement;
    if (!metaTag && content) {
      metaTag = document.createElement("meta");
      if (common) {
        metaTag.name = propName;
      } else {
        metaTag.setAttribute("property", propName);
      }
      document.head.prepend(metaTag);
    }
    if (content) {
      metaTag.content = content;
    } else if (metaTag) {
      metaTag.remove();
    }
  }

  public setData(data: SeoData) {
    const { title, image, lcpImage, type, description, url, noIndex } =
      fixData(data);

    if (import.meta.env.SSR) {
      useSeoMeta({
        title,
        description,
        ogDescription: description,
        ogTitle: title,
        ogImage: image,
        ogUrl: url,
        ogType: type,
        twitterTitle: title,
        twitterDescription: description,
        twitterImage: image,
      } as UseSeoMetaInput);
      useHead({
        link: [
          {
            rel: "canonical",
            href: url,
          },
        ],
      });
      if (lcpImage) {
        useHead({
          link: [
            {
              rel: "preload",
              as: "image",
              fetchpriority: "high",
              href: lcpImage,
            },
          ],
        });
      }
      if (noIndex) {
        useHead({
          meta: [
            {
              name: "robots",
              content: "noindex",
            },
          ],
        });
      }
    } else if (typeof document === "object") {
      document.title = title;
      this.setMeta("og:title", title);
      this.setMeta("og:image", image);
      this.setMeta("og:type", type);
      this.setMeta("og:description", description);
      this.setMeta("description", description, true);
      this.setMeta("og:url", url);

      this.setNoIndex(data.noIndex === true);
    }
  }

  public setNoIndex(apply: boolean) {
    let metaTag = document.querySelector(
      `meta[name="robots"]`,
    ) as HTMLMetaElement;
    if (apply) {
      if (!metaTag) {
        metaTag = document.createElement("meta");
        metaTag.name = "robots";
        document.head.prepend(metaTag);
      }
      metaTag.content = "noindex";
    } else if (metaTag) {
      metaTag.remove();
    }
  }

  public setProduct(product: Product, url: string) {
    this.setData({
      title: product.name,
      image: product.getCoverImage(ProductImage.Sizes.Original), // og no soporta webp, así que obtenemos la original
      lcpImage: product.getCoverImage(ProductImage.Sizes.Medium), // debe coincidir con la imagen de portada de la galaería (sin fullscreen)
      description: product.meta_description,
      url: url,
      type: "product",
    });
  }
}

const seoService = new SeoService();

export default seoService;
